/**
 * Free gift over a threshold: puts the campaign's gift (a Mont Fort hand
 * soap, Jacob 2026-10-15) in the cart once everything else reaches the
 * threshold, and takes it out again when the cart drops back under. The
 * shopper never adds it themselves; one per order, however far over.
 *
 * Config comes from snippets/jr-gift-threshold.liquid (mode 'config'), which
 * reads the shop metafield custom.gift_threshold. The PRICE is not decided
 * here: the app's gift-threshold discount function makes the line free in
 * cart and checkout, from the same metafield. This script only keeps the
 * line in step with the cart, so a failure here can at worst leave a paid
 * soap out, or a soap in that checkout then charges for. Never a free one
 * that should not be.
 *
 * What counts is the same as the function: every line's price BEFORE
 * discounts (original_line_price), except the gift line itself.
 *
 * Runs on page load when the server-rendered cart says something is out of
 * step (config.needs), and after every cart change (shopify:cart:lines-update,
 * which Horizon fires for adds from product pages, quantity changes and
 * removes in the drawer). The change goes through Shopify.actions.updateCart,
 * so the cart drawer re-renders itself exactly as for any other cart change.
 */
(() => {
  if (window.__jrGift) return;
  window.__jrGift = true;

  const el = document.getElementById('jr-gift-config');
  if (!el) return;
  let cfg;
  try {
    cfg = JSON.parse(el.textContent);
  } catch {
    return;
  }
  if (!cfg || !cfg.variant) return;

  // The page can be served from cache across the start or the end. Before the
  // start: do nothing. After the end the discount no longer applies, so a gift
  // still in the cart would be charged at checkout: take it out.
  const notYet = () => Boolean(cfg.start && Date.now() / 1000 < cfg.start);
  const ended = () => Boolean(cfg.end && Date.now() / 1000 > cfg.end);

  const exclude = Array.isArray(cfg.exclude) ? cfg.exclude.map(Number) : [];
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let running = false;
  let again = false;

  async function cartJs() {
    const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error('cart.js ' + res.status);
    return res.json();
  }

  async function update(lines) {
    const actions = window.Shopify && window.Shopify.actions;
    if (actions && actions.updateCart) {
      await actions.updateCart({ lines }, { event: { context: 'standard-action' } });
      return;
    }
    // No standard actions (should not happen on this theme): plain AJAX, then
    // a reload so the drawer and header count are not left showing the old cart.
    for (const l of lines) {
      if (l.id) {
        await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: l.id, quantity: l.quantity }),
        });
      } else {
        await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(l.merchandiseId), quantity: l.quantity, properties: { _gift: cfg.key } }],
          }),
        });
      }
    }
    window.location.reload();
  }

  async function sync() {
    if (notYet()) return;
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      const cart = await cartJs();
      if (cfg.currency && cart.currency && cart.currency !== cfg.currency) return;

      let basket = 0;
      const gifts = [];
      for (const item of cart.items || []) {
        if (item.properties && item.properties._gift === cfg.key) {
          gifts.push(item);
          continue;
        }
        // Sample sale robes never count towards the gift (their variant ids come from the config)
        if (exclude.indexOf(Number(item.variant_id)) !== -1) continue;
        basket += item.original_line_price || 0;
      }

      /** @type {Array<{id?: string, merchandiseId?: string, quantity: number, attributes?: Array<{key: string, value: string}>}>} */
      const lines = [];
      if (!ended() && cfg.threshold > 0 && basket >= cfg.threshold) {
        const keep = gifts.find((g) => String(g.variant_id) === String(cfg.variant));
        if (!keep) {
          lines.push({
            merchandiseId: String(cfg.variant),
            quantity: 1,
            attributes: [{ key: '_gift', value: cfg.key }],
          });
        } else if (keep.quantity !== 1) {
          lines.push({ id: keep.key, quantity: 1 });
        }
        for (const g of gifts) if (g !== keep) lines.push({ id: g.key, quantity: 0 });
      } else {
        for (const g of gifts) lines.push({ id: g.key, quantity: 0 });
      }
      if (lines.length) await update(lines);
    } catch (e) {
      console.warn('[jr-gift]', e);
    } finally {
      running = false;
      if (again) {
        again = false;
        sync();
      }
    }
  }

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const p = event && event.promise;
    if (p && typeof p.then === 'function') {
      p.then(sync, () => {});
    } else {
      sync();
    }
  });

  if (cfg.needs) sync();
})();
